import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { FoundRecordCard, METER_STATUS_LABELS } from '@horof/shared';
import { Alert, Button, Empty, Input, Segmented, Skeleton, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { CommitteeMetersDocument, matchesMeter } from './meters-data';

const refetchMeters = { additionalTypenames: ['Meter'] };

/**
 * The meters, to look up: the round in the reader's order, and those out of
 * it. The order is changed on the desk (docs/changes/41–42).
 */
export function MetersPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'round' | 'other'>('round');
  const [{ data, error }, refetch] = useQuery({
    query: CommitteeMetersDocument,
    context: refetchMeters,
  });
  const all = useMemo(
    () => (data?.meters?.nodes ?? []).flatMap((m) => (m ? [m] : [])),
    [data],
  );
  const round = useMemo(
    () => all.filter((m) => m.meterStatus === 'CONNECTED'),
    [all],
  );
  const other = all.filter((m) => m.meterStatus !== 'CONNECTED');
  const list = filter === 'round' ? round : other;
  const visible = list.filter((m) => matchesMeter(m, search));

  return (
    <div className="page">
      <header className="list-header">
        <div className="title-row">
          <h1>العدادات</h1>
          <Link to="/meters/new">
            <Button type="primary" icon={<PlusOutlined />}>
              عداد جديد
            </Button>
          </Link>
        </div>
        <Input
          size="large"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="بحث: العداد، العضو، العنوان"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Segmented<'round' | 'other'>
          block
          size="large"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'round', label: `في الجولة (${round.length})` },
            { value: 'other', label: `غير مربوطة (${other.length})` },
          ]}
        />
      </header>
      <FoundRecordCard
        text={search}
        href={(f) =>
          f.billId
            ? `/bills/${f.billId}`
            : f.kind === 'meter'
              ? `/meters/${f.id}`
              : f.memberId
                ? `/members/${f.memberId}`
                : null
        }
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message="تعذّر تحميل العدادات"
          action={
            <Button onClick={() => refetch({ requestPolicy: 'network-only' })}>
              أعد المحاولة
            </Button>
          }
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}

      <ol className="member-list">
        {visible.map((m) => (
          <li key={m.id}>
            <Link to={`/meters/${m.id}`} className="member-row">
              {filter === 'round' && (
                <span className="meter-position ltr-num">
                  {round.indexOf(m) + 1}
                </span>
              )}
              <span className="member-main">
                <span className="member-name">{m.member?.fullName}</span>
                <span className="member-sub">
                  العداد {m.label}
                  {m.meterAddress !== m.label ? ` · ${m.meterAddress}` : ''}
                </span>
              </span>
              {m.meterStatus !== 'CONNECTED' && (
                <Tag>{METER_STATUS_LABELS[m.meterStatus]}</Tag>
              )}
            </Link>
          </li>
        ))}
      </ol>
      {data && visible.length === 0 && (
        <Empty description={search ? 'لا يوجد عداد بهذا البحث' : 'لا شيء'} />
      )}
    </div>
  );
}
